import React from "react";
import { Helmet } from "react-helmet-async";
import { useSettings } from "../lib/settings";

/**
 * Reusable SEO head injector.
 * Usage: <SEOHead slug="home" />  OR  <SEOHead title="X" description="Y" />
 * Falls back to global settings + admin-managed page SEO.
 */
export default function SEOHead({ slug, title, description, image, noIndex }) {
  const { settings, pageSeoMap } = useSettings();
  if (!settings) return null;

  const page = slug ? pageSeoMap[slug] : null;
  const finalTitle = title || page?.title || settings.default_meta_title;
  const finalDesc = description || page?.description || settings.default_meta_description;
  const finalKeywords = page?.keywords || settings.default_meta_keywords;
  const finalImage = image || page?.og_image || settings.og_image;
  const canonical = page?.canonical || `${settings.canonical_base_url}${typeof window !== "undefined" ? window.location.pathname : ""}`;
  const robots = noIndex || page?.no_index ? "noindex,nofollow" : "index,follow";

  // Image absolute url
  const absImage = finalImage?.startsWith("http")
    ? finalImage
    : `${settings.canonical_base_url}${finalImage || ""}`;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      {settings.favicon_url && <link rel="icon" href={settings.favicon_url} />}
      {settings.google_search_console_verification && (
        <meta name="google-site-verification" content={settings.google_search_console_verification} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={canonical} />
      {absImage && <meta property="og:image" content={absImage} />}
      <meta property="og:site_name" content={settings.site_title} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      {absImage && <meta name="twitter:image" content={absImage} />}

      {/* Schema.org organization */}
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        name: settings.site_title,
        description: finalDesc,
        url: settings.canonical_base_url,
        logo: settings.favicon_url?.startsWith("http") ? settings.favicon_url : `${settings.canonical_base_url}${settings.favicon_url}`,
        telephone: settings.contact_phone,
        email: settings.contact_email,
        address: { "@type": "PostalAddress", streetAddress: settings.address },
        sameAs: [settings.social_facebook, settings.social_instagram, settings.social_linkedin, settings.social_youtube].filter(Boolean),
      })}</script>
    </Helmet>
  );
}
